import React, { useEffect, useRef, useState } from 'react';
import { IMG } from '../../lib/images';
import SixStepsSection from './SixStepsSection';

const JOURNEY = [
  {
    n: '01',
    t: 'Began with founders',
    d: 'InnovHer began with a focus on helping founders navigate the early stages of building a company.',
  },
  {
    n: '02',
    t: 'Role expanded',
    d: 'As our involvement deepened, our role expanded beyond mentorship and acceleration into venture strategy, operating support, brand, technology, market access, investment readiness and long-term value creation.',
  },
  {
    n: '03',
    t: 'Build alongside',
    d: 'Today, InnovHer works more closely alongside founders and organisations, not only supporting ventures around the business, but participating more deeply in how they are built, strengthened and prepared for scale.',
  },
];

const BELIEFS = [
  {
    title: 'Build Before You Scale',
    body: 'Strong fundamentals should come before aggressive growth.',
  },
  {
    title: 'Evidence Before Assumption',
    body: 'Markets, customers and business models should be validated through real signals.',
  },
  {
    title: 'Readiness Before Capital',
    body: 'Capital should accelerate a strong business, not compensate for a weak one.',
  },
  {
    title: 'Execution Over Advice',
    body: 'Strategy matters only when it translates into action and measurable outcomes.',
  },
  {
    title: 'Partnership Over Transaction',
    body: 'Enduring ventures are built through long-term relationships, not isolated interventions.',
  },
];

const INSTITUTION_PILLARS = [
  {
    title: 'Clear Purpose',
    subtitle: 'Direction & Vision',
    body: 'A venture needs a reason to exist beyond the next raise or release.',
    image: '/new_images/ajay-data-keynote.webp',
    objectPosition: '32% 15%',
    tag: 'Foundational Vision',
    takeaway: 'Anchoring conviction before expanding operations.',
  },
  {
    title: 'Strong Leadership',
    subtitle: 'Accountability & Judgment',
    body: 'Direction, judgment and accountability compound over time.',
    image: '/new_images/innovnxt-bootcamp.webp',
    objectPosition: 'center 40%',
    tag: 'Founder DNA',
    takeaway: 'Decisive operating leadership across high-stakes turning points.',
  },
  {
    title: 'Sound Economics',
    subtitle: 'Capital Discipline',
    body: 'Unit economics and capital discipline decide whether growth is durable.',
    image: '/Innovher-home page/IMG_5117.webp',
    tag: 'Durable Margins',
    takeaway: 'Ensuring sustainable cash generation before growth acceleration.',
  },
  {
    title: 'Capable Teams',
    subtitle: 'Operational Talent',
    body: 'Execution quality follows the people who carry the work each day.',
    image: '/Innovher-home page/2A3A5722.webp',
    tag: 'Talent & Culture',
    takeaway: 'Surrounding founders with high-velocity operators.',
  },
  {
    title: 'Reliable Systems',
    subtitle: 'Repeatable Processes',
    body: 'Repeatable processes turn progress into institutional strength.',
    image: '/new_images/mentorship-table-discussion.webp',
    objectPosition: 'center 25%',
    tag: 'Execution Engine',
    takeaway: 'Turning early wins into standardized playbooks.',
  },
  {
    title: 'Market Relevance',
    subtitle: 'Category Fit & Demand',
    body: 'Customers and category fit keep a venture alive beyond the pitch.',
    image: '/new_images/police-hackathon-relevance.webp',
    objectPosition: 'center 20%',
    tag: 'Market Fit',
    takeaway: 'Building products that create enduring customer retention.',
  },
  {
    title: 'Credible Governance',
    subtitle: 'Trust & Stewardship',
    body: 'Trust, transparency and stewardship unlock long-term partners.',
    image: '/new_images/credible-governance-team.webp',
    objectPosition: 'center 30%',
    tag: 'Stewardship',
    takeaway: 'Institutional trust, clean equity structures, and board integrity.',
  },
];

const MEETS = [
  { left: 'Ideas', right: 'execution' },
  { left: 'Founders', right: 'capability' },
  { left: 'Capital', right: 'readiness' },
  { left: 'Talent', right: 'opportunity' },
];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

function JourneyInteractive() {
  const [active, setActive] = useState(0);
  const item = JOURNEY[active];

  return (
    <div className="mt-12 grid lg:grid-cols-[1fr_1.15fr] gap-8 lg:gap-12 items-start">
      <div className="relative">
        <div className="absolute left-[15px] top-3 bottom-3 w-px bg-white/10" aria-hidden />
        <div
          className="absolute left-[15px] top-3 w-px bg-[#d42b92] transition-all duration-500"
          style={{ height: `calc(${(active / Math.max(JOURNEY.length - 1, 1)) * 100}% - 0.25rem)` }}
          aria-hidden
        />
        <ul className="space-y-2">
          {JOURNEY.map((step, i) => {
            const on = i === active;
            return (
              <li key={step.n}>
                <button
                  type="button"
                  onClick={() => setActive(i)}
                  onMouseEnter={() => setActive(i)}
                  className={`group w-full text-left flex gap-4 rounded-2xl px-3 py-4 transition-colors ${on ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'
                    }`}
                >
                  <span
                    className={`mt-1 relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-all ${on
                      ? 'border-[#d42b92] bg-[#d42b92] text-white scale-110'
                      : 'border-white/20 bg-[#08080a] text-white/50 group-hover:border-[#ff8ec5]/60'
                      }`}
                  >
                    {step.n}
                  </span>
                  <span>
                    <span
                      className={`block text-lg font-semibold transition-colors ${on ? 'text-white' : 'text-white/55 group-hover:text-white/80'
                        }`}
                    >
                      {step.t}
                    </span>
                    {/* <span className="mt-1 block text-xs tracking-[0.18em] uppercase text-white/35">
                      Stage {step.n}
                    </span> */}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div
        key={item.n}
        className="relative min-h-[220px] rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-[#d42b92]/15 via-white/[0.03] to-transparent p-8 sm:p-10 overflow-hidden"
      >
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#d42b92]/25 blur-3xl pointer-events-none" />
        <div className="relative">
          {/* <div className="text-[#ff8ec5] text-xs tracking-[0.28em] uppercase mb-4">
            Our Journey · {item.n}
          </div> */}
          <h3 className="text-2xl sm:text-3xl font-semibold text-white leading-tight">{item.t}</h3>
          <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed">{item.d}</p>
          <div className="mt-8 flex gap-2">
            {JOURNEY.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show journey stage ${i + 1}`}
                onClick={() => setActive(i)}
                className={`h-1.5 rounded-full transition-all ${i === active ? 'w-10 bg-[#d42b92]' : 'w-4 bg-white/20 hover:bg-white/40'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BeliefsInteractive() {
  const [open, setOpen] = useState(0);

  return (
    <div className="mt-12 space-y-2">
      {BELIEFS.map((b, i) => {
        const isOpen = open === i;
        return (
          <button
            key={b.title}
            type="button"
            onClick={() => setOpen(isOpen ? -1 : i)}
            className={`group w-full text-left rounded-2xl border px-5 sm:px-7 py-5 transition-all duration-300 ${isOpen
              ? 'border-[#d42b92]/60 bg-[#d42b92]/10'
              : 'border-white/10 bg-transparent hover:border-white/25 hover:bg-white/[0.02]'
              }`}
          >
            <div className="flex items-start gap-4 sm:gap-6">
              <span
                className={`mt-0.5 text-xs font-semibold tracking-[0.2em] transition-colors ${isOpen ? 'text-[#ff8ec5]' : 'text-white/35 group-hover:text-white/55'
                  }`}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-4">
                  <h3
                    className={`text-lg sm:text-xl font-semibold transition-colors ${isOpen ? 'text-white' : 'text-white/80 group-hover:text-white'
                      }`}
                  >
                    {b.title}
                  </h3>
                  <span
                    className={`shrink-0 text-xl leading-none transition-transform duration-300 ${isOpen ? 'rotate-45 text-[#ff8ec5]' : 'text-white/40'
                      }`}
                    aria-hidden
                  >
                    +
                  </span>
                </div>
                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                >
                  <div className="overflow-hidden">
                    <p className="pt-3 text-sm sm:text-base text-white/60 leading-relaxed max-w-2xl">
                      {b.body}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PerspectiveInteractive() {
  const [active, setActive] = useState(0);
  const pillar = INSTITUTION_PILLARS[active];

  return (
    <div className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
      {/* Left Column: Interactive Pillar Grid / List */}
      <div className="lg:col-span-5 flex flex-col justify-between space-y-2">
        <div className="space-y-2">
          {INSTITUTION_PILLARS.map((p, i) => {
            const on = i === active;
            return (
              <button
                key={p.title}
                type="button"
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className={`w-full group text-left px-4 py-3 rounded-2xl border transition-all duration-300 flex items-center justify-between ${on
                  ? 'border-[#d42b92] bg-gradient-to-r from-[#d42b92]/20 via-[#d42b92]/10 to-transparent shadow-[0_4px_24px_rgba(212,43,146,0.25)] scale-[1.01]'
                  : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/20'
                  }`}
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <span
                    className={`text-xs font-mono tracking-wider font-semibold transition-colors ${on ? 'text-[#ff8ec5]' : 'text-white/40 group-hover:text-white/70'
                      }`}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="truncate">
                    <div
                      className={`text-sm sm:text-base font-medium transition-colors ${on ? 'text-white font-semibold' : 'text-white/75 group-hover:text-white'
                        }`}
                    >
                      {p.title}
                    </div>
                    <div className="text-[11px] text-white/40 truncate">
                      {p.subtitle}
                    </div>
                  </div>
                </div>

                <div className="flex items-center pl-2">
                  <span
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${on
                      ? 'bg-[#d42b92] scale-125 shadow-[0_0_8px_#d42b92]'
                      : 'bg-white/20 group-hover:bg-white/40'
                      }`}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right Column: High-Impact Visual Showcase Frame with Dynamic Photography */}
      <div className="lg:col-span-7 relative">
        {/* Ambient backlight glow */}
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-[#d42b92]/20 blur-[130px] pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-72 h-72 rounded-full bg-purple-600/15 blur-[130px] pointer-events-none" />

        <div className="relative h-[440px] sm:h-[480px] lg:h-full min-h-[460px] rounded-[30px] overflow-hidden border border-white/15 bg-[#0d0d12] shadow-[0_25px_60px_rgba(0,0,0,0.85)] group">
          {/* Main Photo with smooth transition */}
          <img
            key={pillar.image}
            src={pillar.image}
            alt={pillar.title}
            style={{ objectPosition: pillar.objectPosition || 'center center' }}
            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />

          {/* Deep dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-[#08080a]/60 to-black/30 pointer-events-none" />
          <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[30px] pointer-events-none group-hover:ring-[#d42b92]/40 transition-colors duration-500" />

          {/* Top Glass Badges */}
          <div className="absolute top-5 left-5 right-5 flex items-center justify-between z-10">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/15 shadow-lg">
              <span className="w-2 h-2 rounded-full bg-[#d42b92] animate-pulse" />
              <span className="text-[11px] font-semibold tracking-wider uppercase text-white/90">
                Pillar {String(active + 1).padStart(2, '0')} / 07
              </span>
            </div>

            <span className="px-3.5 py-1.5 rounded-full text-[11px] font-semibold tracking-wider uppercase bg-black/70 backdrop-blur-md border border-white/15 text-[#ff8ec5]">
              {pillar.tag}
            </span>
          </div>

          {/* Bottom Frosted Glass Card with Details & Insight */}
          <div className="absolute bottom-5 left-5 right-5 z-10 p-5 sm:p-6 rounded-2xl bg-black/75 backdrop-blur-xl border border-white/15 shadow-2xl transition-all duration-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-[#ff8ec5] uppercase tracking-widest">
                {pillar.subtitle}
              </span>
              <span className="text-[11px] text-white/50">Core Institutional Principle</span>
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              {pillar.title}
            </h3>

            <p className="mt-2 text-sm sm:text-base text-white/80 leading-relaxed">
              {pillar.body}
            </p>

            <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-white/60">
              <span className="text-[#d42b92] font-bold">✦</span>
              <span>{pillar.takeaway}</span>
            </div>

            {/* Mini thumbnail indicator dots / preview */}
            <div className="mt-4 flex items-center gap-2 pt-2 border-t border-white/5">
              {INSTITUTION_PILLARS.map((item, idx) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => setActive(idx)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${idx === active
                    ? 'w-8 bg-[#d42b92]'
                    : 'w-2 bg-white/20 hover:bg-white/40'
                    }`}
                  aria-label={`Go to pillar ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BuildingTowardInteractive() {
  const [active, setActive] = useState(0);
  const pair = MEETS[active];

  return (
    <div className="mt-12">
      <div className="grid sm:grid-cols-2 gap-3">
        {MEETS.map((m, i) => {
          const on = i === active;
          return (
            <button
              key={`${m.left}-${m.right}`}
              type="button"
              onClick={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
              className={`relative overflow-hidden rounded-2xl border px-5 py-6 text-left transition-all duration-300 ${on
                ? 'border-[#d42b92]/70 bg-[#d42b92]/15'
                : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                }`}
            >
              {on ? (
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,43,146,0.25),transparent_55%)] pointer-events-none" />
              ) : null}
              <div className="relative flex items-center justify-between gap-3">
                <span className={`text-lg sm:text-xl font-semibold ${on ? 'text-white' : 'text-white/75'}`}>
                  {m.left}
                </span>
                <span
                  className={`text-[10px] tracking-[0.25em] uppercase transition-colors ${on ? 'text-[#ff8ec5]' : 'text-white/30'
                    }`}
                >
                  meet
                </span>
                <span className={`text-lg sm:text-xl font-semibold ${on ? 'text-[#ff8ec5]' : 'text-white/55'}`}>
                  {m.right}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div
        key={`${pair.left}-${pair.right}`}
        className="mt-6 rounded-[1.75rem] border border-white/10 bg-gradient-to-r from-[#d42b92]/20 via-white/[0.03] to-transparent px-6 py-8 sm:px-10"
      >
        <p className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight">
          {pair.left}{' '}
          <span className="text-white/40 font-normal">meet</span>{' '}
          <span className="text-[#ff8ec5]">{pair.right}</span>
          <span className="text-white/40">.</span>
        </p>
      </div>
    </div>
  );
}

function SpotlightSection({ children, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--sx', `${e.clientX - r.left}px`);
      el.style.setProperty('--sy', `${e.clientY - r.top}px`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section
      ref={ref}
      className={`relative overflow-hidden ${className}`}
      style={{
        backgroundImage:
          'radial-gradient(520px circle at var(--sx, 50%) var(--sy, 30%), rgba(212,43,146,0.12), transparent 55%)',
      }}
    >
      {children}
    </section>
  );
}

export default function About() {
  return (
    <>
      {/* Interactive 6 Steps: Origin, Belief, Difference, Journey, Patrons, Leadership */}
      <SixStepsSection />

      {/* Intro */}
      <section className="relative bg-[#08080a] py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="reveal-up relative order-2 lg:order-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-[480px] sm:h-[540px] lg:h-[580px]">
              {/* Left: 1 tall full-height image */}
              <div className="rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02] group relative h-full">
                <img
                  src={IMG.aboutGrid[0]}
                  alt="InnovHer founders and ecosystem"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>

              {/* Right: 2 stacked equal-height images perfectly aligned */}
              <div className="flex flex-col gap-4 h-full">
                <div className="flex-1 rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02] group relative">
                  <img
                    src={IMG.aboutGrid[1]}
                    alt="AI Day for Startups"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>

                <div className="flex-1 rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02] group relative">
                  <img
                    src={IMG.aboutGrid[2] || IMG.aboutGrid[0]}
                    alt="InnovHer Strategic Roundtable"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            {/* <SectionEyebrow>About InnovHer</SectionEyebrow> */}
            <h2 className="reveal-up font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
              Built to Build ventures that{' '}
              <span className="text-[#d42b92]">last</span>.
            </h2>
            <div className="reveal-up mt-6 space-y-5 text-white/60 leading-relaxed text-base sm:text-lg max-w-xl">
              <p>
                InnovHer is a venture-building company created to help ambitious ideas become
                stronger, more structured and scalable businesses.
              </p>
              <p>
                We began by working closely with early-stage founders through acceleration,
                mentorship and ecosystem support. Over time, that role evolved.
              </p>
              <p>
                We realised that founders rarely need one more advisor, programme or introduction.
                They need the right combination of strategy, execution, technology, talent, markets
                and capital readiness working together at the right time.
              </p>
              <p className="text-white font-medium">That insight shaped the InnovHer of today.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Journey, interactive */}
      <section className="relative bg-[#08080a] py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-12">
            <div className="lg:col-span-7">
              <SectionEyebrow>Our Journey</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                From supporting startups to{' '}
                <span className="text-[#d42b92]">building ventures</span>
              </h2>
              <p className="mt-4 text-white/45 text-sm">Hover or click a stage to explore the path.</p>
            </div>

            <div className="lg:col-span-5">
              <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 backdrop-blur-sm relative overflow-hidden shadow-xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#d42b92]/10 rounded-full blur-2xl pointer-events-none" />
                <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                  Venture building is not a single leap. It is an evolving craft that deepens with every cohort, partnership, and operational challenge we solve together.
                </p>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-white/50">
                  <span className="text-[#ff8ec5] font-semibold tracking-wider uppercase">3 Evolutionary Phases</span>
                  <span>Jaipur · National Impact</span>
                </div>
              </div>
            </div>
          </div>
          <div className="reveal-up">
            <JourneyInteractive />
          </div>
        </div>
      </section>

      {/* Why we exist */}
      <SpotlightSection className="bg-[#08080a] py-20 lg:py-28 border-t border-white/5">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="reveal-up max-w-3xl">
            <SectionEyebrow>Why We Exist</SectionEyebrow>
            <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
              To turn potential into{' '}
              <span className="text-[#d42b92]">enterprise</span>.
          </h2>
            <div className="mt-6 space-y-5 text-white/60 text-base sm:text-lg leading-relaxed">
              <p>Entrepreneurial ambition is not scarce.</p>
              <p>
                What is often missing is the structure, capability, discipline and access required
                to convert that ambition into a sustainable enterprise.
              </p>
              <p className="text-white font-medium">InnovHer exists to bridge that gap.</p>
              <p>
                We believe strong ventures are built by combining clear thinking, strong
                fundamentals, capable people, disciplined execution and access to the right
                ecosystem.
              </p>
              <p>Our role is to help bring those pieces together.</p>
            </div>
          </div>
          {/* Redesigned Luxury Bento Grid */}
          <div className="reveal-up relative">
            {/* Ambient atmospheric backlight glows */}
            <div className="absolute -top-12 -right-12 w-80 h-80 rounded-full bg-gradient-to-br from-[#d42b92]/25 to-[#ff8ec5]/10 blur-[130px] pointer-events-none" />
            <div className="absolute -bottom-12 -left-12 w-80 h-80 rounded-full bg-gradient-to-tr from-[#d42b92]/20 to-purple-600/10 blur-[130px] pointer-events-none" />

            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 h-[520px] sm:h-[580px] lg:h-[600px]">
              {/* Left Column: Hero Portrait Card */}
              <div className="group relative h-full rounded-[28px] overflow-hidden border border-white/10 bg-[#0d0d12]/90 shadow-[0_20px_50px_rgba(0,0,0,0.85)] hover:border-[#d42b92]/60 hover:shadow-[0_20px_60px_rgba(212,43,146,0.25)] transition-all duration-700">
                <img
                  src={IMG.homePhotos.msmeHonours}
                  alt="InnovHer leadership at MSME Honours 2025"
                  className="w-full h-full object-cover object-top transform group-hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />

                {/* Subtle scrim overlays for visual depth & legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-black/20 to-black/30 pointer-events-none" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[28px] pointer-events-none group-hover:ring-[#d42b92]/40 transition-colors duration-500" />

                {/* Floating Top Badge */}
                {/* <div className="absolute top-4 left-4 z-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/15 shadow-lg">
                    <span className="w-2 h-2 rounded-full bg-[#d42b92] animate-pulse" />
                    <span className="text-[11px] font-semibold tracking-wider uppercase text-white/90">
                      Active Cohort · Jaipur
                    </span>
                  </div>
                </div> */}

                {/* Bottom Frosted Glass Caption Panel */}
                {/* <div className="absolute bottom-4 left-4 right-4 z-10 p-4 rounded-2xl bg-black/65 backdrop-blur-md border border-white/10 group-hover:border-[#d42b92]/40 transition-all duration-500">
                  <div className="text-sm sm:text-base font-bold text-white group-hover:text-[#ff8ec5] transition-colors leading-tight">
                    Venture Capability Building
                  </div>
                  <p className="mt-1 text-xs text-white/70 line-clamp-2 leading-relaxed">
                    Direct hands-on operating support, systems, and execution discipline for founders.
                  </p>
                </div> */}

              </div>

              {/* Right Column: Two Balanced Stacked Cards */}
              <div className="flex flex-col gap-4 sm:gap-5 h-full">
                {/* Top Card: AI Day for Startups */}
                <div className="flex-1 group relative rounded-[28px] overflow-hidden border border-white/10 bg-[#0d0d12]/90 shadow-[0_15px_35px_rgba(0,0,0,0.7)] hover:border-[#d42b92]/60 hover:shadow-[0_15px_45px_rgba(212,43,146,0.22)] transition-all duration-700">
                  <img
                    src={IMG.homePhotos.aiDaySpeaker}
                    alt="Google for Startups AI Day Jaipur"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />

                  {/* Scrim overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-black/20 to-black/30 pointer-events-none" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[28px] pointer-events-none group-hover:ring-[#d42b92]/40 transition-colors duration-500" />

                  {/* Top Badge */}
                  {/* <div className="absolute top-4 left-4 z-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/15 shadow-lg">
                      <span className="text-[11px] font-semibold tracking-wider uppercase text-[#ff8ec5]">
                        Google for Startups
                      </span>
                    </div>
                  </div> */}

                  {/* Bottom Caption Panel */}
                  {/* <div className="absolute bottom-3.5 left-3.5 right-3.5 z-10 p-3 sm:p-3.5 rounded-2xl bg-black/65 backdrop-blur-md border border-white/10 group-hover:border-[#d42b92]/40 transition-all duration-500">
                    <div className="text-xs sm:text-sm font-bold text-white group-hover:text-[#ff8ec5] transition-colors leading-tight">
                      AI Day for Startups · Jaipur
                    </div>
                    <p className="mt-0.5 text-[11px] text-white/70 line-clamp-1">
                      Connecting regional ventures to global tech roadmaps & ecosystem partners.
                    </p>
                  </div> */}
                </div>

                {/* Bottom Card: Strategic Roundtable */}
                <div className="flex-1 group relative rounded-[28px] overflow-hidden border border-white/10 bg-[#0d0d12]/90 shadow-[0_15px_35px_rgba(0,0,0,0.7)] hover:border-[#d42b92]/60 hover:shadow-[0_15px_45px_rgba(212,43,146,0.22)] transition-all duration-700">
                  <img
                    src={IMG.homePhotos.startupPartnership}
                    alt="InnovHer startup collaboration and ecosystem partnership"
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />

                  {/* Scrim overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-black/20 to-black/30 pointer-events-none" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[28px] pointer-events-none group-hover:ring-[#d42b92]/40 transition-colors duration-500" />

                  {/* Top Badge */}
                  {/* <div className="absolute top-4 left-4 z-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/15 shadow-lg">
                      <span className="text-[11px] font-semibold tracking-wider uppercase text-white/90">
                        Studio I · Boardroom
                      </span>
                    </div>
                  </div> */}

                  {/* Bottom Caption Panel */}
                  {/* <div className="absolute bottom-3.5 left-3.5 right-3.5 z-10 p-3 sm:p-3.5 rounded-2xl bg-black/65 backdrop-blur-md border border-white/10 group-hover:border-[#d42b92]/40 transition-all duration-500">
                    <div className="text-xs sm:text-sm font-bold text-white group-hover:text-[#ff8ec5] transition-colors leading-tight">
                      Strategic Dialogues & Capital Readiness
                    </div>
                    <p className="mt-0.5 text-[11px] text-white/70 line-clamp-1">
                      Institutional capital, governance, and market access for scale.
                    </p>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Floating Central Trust Pill */}
            {/* <div className="hidden sm:flex absolute -bottom-4 left-1/2 -translate-x-1/2 bg-[#0c0c10]/95 backdrop-blur-md border border-white/15 px-5 py-2 rounded-full shadow-[0_10px_30px_rgba(0,0,0,0.8)] z-20 items-center gap-2.5 hover:border-[#d42b92]/50 transition-colors">
              <span className="w-2 h-2 rounded-full bg-[#d42b92] animate-ping" />
              <span className="text-xs font-semibold text-white/90 tracking-wide">
                Where Ambition Becomes Enterprise
              </span>
            </div> */}
          </div>
        </div>
      </SpotlightSection>

      {/* What we believe, accordion */}
      <section className="relative bg-[#08080a] py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-12">
            <div className="lg:col-span-7">
              <SectionEyebrow>What We Believe</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Principles that shape how we build
              </h2>
              <p className="mt-4 text-white/45 text-sm">Open each principle to read how we work.</p>
            </div>

            <div className="lg:col-span-5">
              <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 backdrop-blur-sm relative overflow-hidden shadow-xl">
                <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                  Beliefs are not slogans. They are operating commitments that govern how we evaluate risk, deploy capital, and stand alongside founders.
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-[#ff8ec5] font-semibold uppercase tracking-wider">
                  <span>✦ 5 Non-Negotiable Operating Tenets</span>
                </div>
              </div>
            </div>
          </div>
          <div className="reveal-up">
            <BeliefsInteractive />
          </div>
        </div>
      </section>

      {/* Perspective, interactive pillars */}
      <section className="relative bg-[#08080a] py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-12">
            <div className="lg:col-span-7">
              <SectionEyebrow>Our Perspective</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Ventures are institutions in the{' '}
                <span className="text-[#d42b92]">making</span>.
              </h2>
            </div>

            {/* <div className="lg:col-span-5">
              <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 backdrop-blur-sm relative overflow-hidden shadow-xl space-y-3">
                <p className="text-white font-medium text-base sm:text-lg">We look beyond the immediate milestone.</p>
                <p className="text-xs sm:text-sm text-white/65 leading-relaxed">
                  A venture is not successful simply because it launches, raises capital or grows quickly. Over time, enduring companies require clear structure, capable leadership, and disciplined execution.
                </p>
                <div className="pt-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#ff8ec5]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d42b92] animate-pulse" />
                  <span>7 Core Institutional Pillars</span>
                </div>
              </div>
            </div> */}
          </div>

          <div className="reveal-up">
            <PerspectiveInteractive />
          </div>
          <p className="reveal-up mt-8 text-white/50 text-base max-w-2xl leading-relaxed">
            That is the lens through which we approach venture building.
          </p>
        </div>
      </section>

      {/* What We Are Building Toward */}
      <SpotlightSection className="bg-[#08080a] py-20 lg:py-28 border-t border-white/5">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-10">
            <div className="reveal-up lg:col-span-7 max-w-3xl">
              <SectionEyebrow>What We Are Building Toward</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                An institution for{' '}
                <span className="text-[#d42b92]">venture building</span>.
              </h2>
              <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
                Our ambition is to build an organisation capable of repeatedly identifying
                opportunity, strengthening capability and creating long-term enterprise value.
              </p>
              <p className="mt-4 text-white font-medium">An institution where:</p>
            </div>
            <div className="reveal-up lg:col-span-5 hidden md:block">
              <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[4/3]">
                <img
                  src={IMG.homePhotos.teamGathering}
                  alt="InnovHer team"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <div className="reveal-up">
            <BuildingTowardInteractive />
          </div>

          <p className="reveal-up mt-10 text-white/55 text-base sm:text-lg leading-relaxed max-w-2xl">
            And where promising ventures are given the structure, support and environment required
            to become enduring businesses.
          </p>
        </div>
      </SpotlightSection>

      {/* Jaipur */}
      <section className="relative bg-[#08080a] py-20 lg:py-28 border-t border-white/5 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[640px] h-[320px] rounded-full bg-[#d42b92]/15 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up grid lg:grid-cols-2 gap-0 rounded-3xl border border-white/10 overflow-hidden bg-gradient-to-br from-white/[0.04] to-transparent">
            <div className="p-8 sm:p-10 lg:p-14 flex flex-col justify-center">
              <SectionEyebrow>Where we build from</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Rooted in Jaipur.{' '}
                <span className="text-[#d42b92]">Connected to a wider India.</span>
              </h2>
              <p className="mt-5 text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
                InnovHer has grown from Jaipur while working across a wider entrepreneurial
                ecosystem.
              </p>
            </div>
            <div className="relative min-h-[260px] lg:min-h-full">
              <img
                src={IMG.homePhotos.d2cSummit}
                alt="InnovHer at Bizz Expo & Summit Jaipur"
                className="absolute inset-0 w-full h-full object-cover object-[center_35%]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#08080a]/40 to-transparent lg:from-[#08080a]/25" />
            </div>
        </div>
      </div>
    </section>
    </>
  );
}
